import { MATERIAL_PREFERENCES, type MaterialPreference } from "@shared/quote";

export const PARTITION_BUDGET_UPDATED_LABEL = "October 2026";
/** Owner-approved current planning context only; final project timing is confirmed in the written quote. */
export const PARTITION_BUDGET_TYPICAL_LEAD_TIME = "2–4 weeks";
export const PARTITION_BUDGET_LEAD_TIME_BOUNDARY = "Planning context reviewed October 2026. It is not a project-specific lead-time, availability, or delivery commitment; written quote review confirms timing.";

export const partitionBudgetMaterials = [
  {
    id: "powder-coated-steel",
    name: "Powder Coated Steel",
    materialPreference: "Powder Coated Steel",
    summary: "A practical conventional choice for commercial restroom partition planning.",
    materialPath: "/bathroom-partitions/powder-coated-steel",
    planningGuideUrl: "/manus-storage/Partition-Stalls-Material-Guide-Powder-Coated-Steel-Clean-Planning_c3697072.pdf",
  },
  {
    id: "plastic-laminate",
    name: "Plastic Laminate",
    materialPreference: "Plastic Laminate",
    summary: "A color and finish-led option for interior commercial restroom projects.",
    materialPath: "/bathroom-partitions/plastic-laminate",
    planningGuideUrl: "/manus-storage/Partition-Stalls-Material-Guide-Plastic-Laminate-Clean-Planning_27ca77b2.pdf",
  },
  {
    id: "solid-plastic-hdpe",
    name: "Solid Plastic (HDPE)",
    materialPreference: "Solid Plastic (HDPE)",
    summary: "A moisture-resistant material direction for demanding restroom environments.",
    materialPath: "/bathroom-partitions/solid-plastic-hdpe",
    planningGuideUrl: "/manus-storage/Partition-Stalls-Material-Guide-Solid-Plastic-HDPE-Clean-Planning_0ee4316f.pdf",
  },
  {
    id: "phenolic-black-core",
    name: "Phenolic — Black Core",
    materialPreference: "Phenolic",
    summary: "A black-core phenolic direction for commercial partition planning.",
    materialPath: "/bathroom-partitions/phenolic",
    planningGuideUrl: "/manus-storage/Partition-Stalls-Material-Guide-Phenolic-Clean-Planning_2e57a8da.pdf",
  },
  {
    id: "phenolic-color-thru",
    name: "Phenolic — Color-Thru",
    materialPreference: "Phenolic",
    summary: "A color-thru phenolic direction for commercial partition planning.",
    materialPath: "/bathroom-partitions/phenolic",
    planningGuideUrl: "/manus-storage/Partition-Stalls-Material-Guide-Phenolic-Clean-Planning_2e57a8da.pdf",
  },
  {
    id: "stainless-steel",
    name: "Stainless Steel",
    materialPreference: "Stainless Steel",
    summary: "A durable metal material direction for commercial restroom partition projects.",
    materialPath: "/bathroom-partitions/stainless-steel",
    planningGuideUrl: "/manus-storage/Partition-Stalls-Material-Guide-Stainless-Steel-Clean-Planning_a3521873.pdf",
  },
] as const;

export type PartitionBudgetMaterialId = (typeof partitionBudgetMaterials)[number]["id"];
export type PartitionBudgetPrivacy = "standard" | "extra-privacy";
export type PartitionBudgetInstallation = "not-included" | "included";
export type PartitionBudgetAccessibility = "standard" | "accessibility";
export type PartitionBudgetStallCount = 1 | 2 | 3 | 4 | 5 | 6;

export const partitionBudgetStallCounts: readonly PartitionBudgetStallCount[] = [1, 2, 3, 4, 5, 6];

/** Product-detail references for comparison only; final manufacturer, finish, and configuration are quoted per project. */
export const partitionBudgetMaterialProductVisuals: Record<PartitionBudgetMaterialId, { src: string; alt: string; caption: string }> = {
  "powder-coated-steel": {
    src: "/manus-storage/powder-coated-steel-owner-detail_c71c4221.avif",
    alt: "Powder Coated Steel toilet partition panel reference",
    caption: "Product detail reference",
  },
  "plastic-laminate": {
    src: "/manus-storage/plastic-laminate-owner-detail_9037c5ab.avif",
    alt: "Plastic Laminate toilet partition panel detail",
    caption: "Product detail reference",
  },
  "solid-plastic-hdpe": {
    src: "/manus-storage/solid-plastic-hdpe-owner-detail_d1169c2f.webp",
    alt: "Solid Plastic HDPE toilet partition panel detail",
    caption: "Product detail reference",
  },
  "phenolic-black-core": {
    src: "/manus-storage/phenolic-black-core-owner-detail_30d0b5bb.avif",
    alt: "Black Core Phenolic toilet partition panel detail",
    caption: "Product detail reference",
  },
  "phenolic-color-thru": {
    src: "/manus-storage/phenolic-color-thru-product-detail_83ec7b23.png",
    alt: "Color-Thru Phenolic toilet partition panel detail",
    caption: "Product detail reference",
  },
  "stainless-steel": {
    src: "/manus-storage/stainless-steel-owner-detail_69fa7a49.jpg",
    alt: "Stainless Steel toilet partition panel detail",
    caption: "Product detail reference",
  },
};

/** Owner-approved fixed planning additions, effective October 25, 2026. */
export const EXTRA_PRIVACY_ADDITION_PER_STALL_CENTS = 15_000;
export const INSTALLATION_ADDITION_PER_STALL_CENTS = 35_000;

export type PartitionBudgetInput = {
  materialId: PartitionBudgetMaterialId;
  stalls: PartitionBudgetStallCount;
  accessibility: PartitionBudgetAccessibility;
  privacy: PartitionBudgetPrivacy;
  installation: PartitionBudgetInstallation;
};

/** A visitor-initiated PDF guide carries this exact calculator selection; it never creates a lead or quote. */
export function getPartitionBudgetMaterialGuidePdfUrl(input: PartitionBudgetInput) {
  const query = new URLSearchParams({
    materialId: input.materialId,
    stalls: String(input.stalls),
    accessibility: input.accessibility,
    privacy: input.privacy,
    installation: input.installation,
  });
  return `/api/v1/material-guide.pdf?${query.toString()}`;
}

type PlanningRange = {
  lowCents: number;
  highCents: number;
  typicalLowCents?: number;
  typicalHighCents?: number;
};

export type PartitionBudgetEstimate = PartitionBudgetInput & PlanningRange & {
  basePerCompartment: PlanningRange;
  perCompartment: PlanningRange;
  privacyAdditionPerStallCents: number;
  installationAdditionPerStallCents: number;
  selectedAdditionsPerStallCents: number;
  selectedAdditionsLabel: string;
  basisLabel: string;
};

/**
 * Owner-approved base material planning allowances. The lower end reflects the
 * October 2026 15% uplift; the original approved upper ceiling is retained.
 * These are per-compartment planning figures, not checkout prices, quotes, or
 * promises for a particular manufacturer, size, mounting system, or jobsite.
 */
const approvedBasePlanningRanges: Readonly<Record<PartitionBudgetMaterialId, PlanningRange>> = {
  "powder-coated-steel": { lowCents: 46_000, typicalLowCents: 69_000, typicalHighCents: 72_000, highCents: 80_000 },
  "plastic-laminate": { lowCents: 57_500, typicalLowCents: 79_350, typicalHighCents: 80_000, highCents: 90_000 },
  "solid-plastic-hdpe": { lowCents: 74_750, typicalLowCents: 121_900, typicalHighCents: 141_000, highCents: 165_000 },
  "phenolic-black-core": { lowCents: 103_500, typicalLowCents: 135_700, typicalHighCents: 144_000, highCents: 180_000 },
  "phenolic-color-thru": { lowCents: 115_000, typicalLowCents: 149_500, typicalHighCents: 160_000, highCents: 190_000 },
  "stainless-steel": { lowCents: 110_975, highCents: 220_000 },
};

export function getPartitionBudgetMaterial(materialId: PartitionBudgetMaterialId) {
  return partitionBudgetMaterials.find((material) => material.id === materialId);
}

export function getPartitionBudgetMaterialPriceRange(materialId: PartitionBudgetMaterialId) {
  return approvedBasePlanningRanges[materialId];
}

function applyFixedAddition(range: PlanningRange, additionPerStallCents: number): PlanningRange {
  return {
    lowCents: range.lowCents + additionPerStallCents,
    highCents: range.highCents + additionPerStallCents,
    ...(range.typicalLowCents !== undefined && range.typicalHighCents !== undefined
      ? {
          typicalLowCents: range.typicalLowCents + additionPerStallCents,
          typicalHighCents: range.typicalHighCents + additionPerStallCents,
        }
      : {}),
  };
}

export function getPartitionBudgetPrivacyLabel(privacy: PartitionBudgetPrivacy) {
  return privacy === "extra-privacy"
    ? "Extra privacy (+$150 per stall)"
    : "Standard privacy";
}

export function getPartitionBudgetInstallationLabel(installation: PartitionBudgetInstallation) {
  return installation === "included"
    ? "Installation included (+$350 per stall)"
    : "Installation not included";
}

export function getPartitionBudgetAccessibilityLabel(accessibility: PartitionBudgetAccessibility) {
  return accessibility === "accessibility" ? "Accessibility configuration" : "Standard configuration";
}

export function getPartitionBudgetEstimate(input: PartitionBudgetInput): PartitionBudgetEstimate | undefined {
  if (!isPartitionBudgetInput(input)) return undefined;

  const basePerCompartment = approvedBasePlanningRanges[input.materialId];
  const privacyAdditionPerStallCents = input.privacy === "extra-privacy" ? EXTRA_PRIVACY_ADDITION_PER_STALL_CENTS : 0;
  const installationAdditionPerStallCents = input.installation === "included" ? INSTALLATION_ADDITION_PER_STALL_CENTS : 0;
  const selectedAdditionsPerStallCents = privacyAdditionPerStallCents + installationAdditionPerStallCents;
  const perCompartment = applyFixedAddition(basePerCompartment, selectedAdditionsPerStallCents);
  const multiplier = input.stalls;
  const selectedAdditions = [
    input.privacy === "extra-privacy" ? "extra privacy" : null,
    input.installation === "included" ? "installation" : null,
  ].filter((value): value is string => Boolean(value));

  return {
    ...input,
    lowCents: perCompartment.lowCents * multiplier,
    highCents: perCompartment.highCents * multiplier,
    ...(perCompartment.typicalLowCents !== undefined && perCompartment.typicalHighCents !== undefined
      ? {
          typicalLowCents: perCompartment.typicalLowCents * multiplier,
          typicalHighCents: perCompartment.typicalHighCents * multiplier,
        }
      : {}),
    basePerCompartment,
    perCompartment,
    privacyAdditionPerStallCents,
    installationAdditionPerStallCents,
    selectedAdditionsPerStallCents,
    selectedAdditionsLabel: selectedAdditions.length > 0 ? selectedAdditions.join(" + ") : "No additions selected",
    basisLabel: selectedAdditions.length > 0
      ? "Material planning range with selected additions"
      : "Material planning range",
  };
}

export function formatPartitionBudgetCurrency(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

export function isPartitionBudgetInput(value: unknown): value is PartitionBudgetInput {
  if (!value || typeof value !== "object") return false;
  const input = value as Record<string, unknown>;
  const materialId = input.materialId;
  const stalls = input.stalls;
  const accessibility = input.accessibility;
  const privacy = input.privacy;
  const installation = input.installation;

  if (typeof materialId !== "string" || !partitionBudgetMaterials.some((material) => material.id === materialId)) return false;
  if (typeof stalls !== "number" || !partitionBudgetStallCounts.includes(stalls as PartitionBudgetStallCount)) return false;
  if (accessibility !== "standard" && accessibility !== "accessibility") return false;
  if (privacy !== "standard" && privacy !== "extra-privacy") return false;
  if (installation !== "not-included" && installation !== "included") return false;
  return true;
}

export function getPartitionBudgetQuoteMaterialPreference(materialId: PartitionBudgetMaterialId): MaterialPreference {
  const preference = getPartitionBudgetMaterial(materialId)?.materialPreference;
  if (!preference || !MATERIAL_PREFERENCES.includes(preference as MaterialPreference)) {
    throw new Error("Unsupported partition budget material.");
  }
  return preference as MaterialPreference;
}

export const partitionBudgetEstimateCount = partitionBudgetMaterials.length;
